import * as React from "react";
import type { 
  ZipcodePerformanceTableProps, 
  ZipcodeData, 
  GeographicTableColumn, 
  TableSortConfig, 
  TableFilterConfig 
} from '../../types/geographic';

// Table columns configuration
const COLUMNS: GeographicTableColumn[] = [
  { key: 'rank', label: 'Rank', sortable: true, width: '80px' },
  { key: 'zipcode', label: 'Zipcode', sortable: true, width: '100px' },
  { key: 'city_name', label: 'City', sortable: true },
  { key: 'state_name', label: 'State', sortable: true, width: '120px' },
  { key: 'user_count', label: 'Total Users', sortable: true, format: 'number' },
  { key: 'client_count', label: 'Clients', sortable: true, format: 'number' },
  { key: 'professional_count', label: 'Professionals', sortable: true, format: 'number' },
  { key: 'revenue', label: 'Revenue', sortable: true, format: 'currency' },
  { key: 'roi', label: 'ROI', sortable: true, format: 'percentage' }
];

// Utility functions
const formatValue = (value: any, format?: GeographicTableColumn['format']): string => {
  if (value == null) return '-';
  
  switch (format) {
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value.toString();
    case 'currency':
      return typeof value === 'number' ? 
        new Intl.NumberFormat('en-US', { 
          style: 'currency', 
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value) : '-';
    case 'percentage':
      return typeof value === 'number' ? `${value.toFixed(1)}%` : '-';
    default:
      return value.toString();
  }
};

const sortData = (data: ZipcodeData[], sortConfig: TableSortConfig): ZipcodeData[] => {
  return [...data].sort((a, b) => {
    const aValue = (a as any)[sortConfig.column];
    const bValue = (b as any)[sortConfig.column];
    
    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    
    // Sort based on type
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // String comparison
    const aStr = aValue.toString().toLowerCase();
    const bStr = bValue.toString().toLowerCase();
    
    if (sortConfig.direction === 'asc') {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });
};

const filterData = (data: ZipcodeData[], filters: TableFilterConfig): ZipcodeData[] => {
  return data.filter(zipcode => {
    // Search term filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableText = `${zipcode.zipcode} ${zipcode.city_name} ${zipcode.state_name} ${zipcode.state_code}`.toLowerCase();
      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }
    
    // Min user count filter
    if (filters.minUserCount && zipcode.user_count < filters.minUserCount) {
      return false;
    }
    
    // States filter
    if (filters.states?.length && !filters.states.includes(zipcode.state_code)) {
      return false;
    }
    
    return true;
  });
};

// Zipcode validation utility
const isValidZipcode = (zipcode: string): boolean => {
  return /^\d{5}(-\d{4})?$/.test(zipcode);
};

// Table header component
const TableHeader: React.FC<{
  column: GeographicTableColumn;
  sortConfig: TableSortConfig | null;
  onSort: (column: string, direction: 'asc' | 'desc') => void;
}> = ({ column, sortConfig, onSort }) => {
  const isSorted = sortConfig?.column === column.key;
  const currentDirection = isSorted ? sortConfig!.direction : null;
  
  const handleSort = () => {
    if (!column.sortable) return;
    
    let newDirection: 'asc' | 'desc' = 'asc';
    if (isSorted && currentDirection === 'asc') {
      newDirection = 'desc';
    }
    
    onSort(column.key, newDirection);
  };

  return (
    <th
      style={{ width: column.width }}
      className={`${column.sortable ? 'cursor-pointer user-select-none' : ''}`}
      onClick={handleSort}
    >
      <div className="d-flex align-items-center justify-content-between">
        <span>{column.label}</span>
        {column.sortable && (
          <div className="ms-1">
            {isSorted ? (
              <i className={`fas fa-sort-${currentDirection === 'asc' ? 'up' : 'down'} text-primary`} aria-hidden="true"></i>
            ) : (
              <i className="fas fa-sort text-muted" aria-hidden="true"></i>
            )}
          </div>
        )}
      </div>
    </th>
  );
};

// Table row component
const TableRow: React.FC<{
  zipcode: ZipcodeData;
  columns: GeographicTableColumn[];
  onClick?: (zipcode: ZipcodeData) => void;
  index: number;
}> = React.memo(({ zipcode, columns, onClick, index }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(zipcode);
    }
  };

  // Add computed rank
  const zipcodeWithRank = {
    ...zipcode,
    rank: index + 1
  };

  const validZip = isValidZipcode(zipcode.zipcode);

  return (
    <tr 
      className={`${onClick ? 'cursor-pointer table-row-hover' : ''}`}
      onClick={handleClick}
    >
      {columns.map(column => (
        <td key={column.key}>
          {column.key === 'zipcode' ? (
            <div className="d-flex align-items-center">
              <div>
                <div className={`fw-semibold font-monospace ${!validZip ? 'text-warning' : ''}`}>
                  {zipcode.zipcode}
                </div>
                {zipcode.coordinates && (
                  <small className="text-muted">
                    {zipcode.coordinates.lat.toFixed(2)}, {zipcode.coordinates.lng.toFixed(2)}
                  </small>
                )}
              </div>
            </div>
          ) : column.key === 'city_name' ? (
            <div>
              <div>{zipcode.city_name}</div>
              {!validZip && (
                <small className="text-warning">
                  <i className="fas fa-exclamation-triangle me-1" aria-hidden="true"></i>
                  Invalid format
                </small>
              )}
            </div>
          ) : column.key === 'state_name' ? (
            <div>
              <div>{zipcode.state_name}</div>
              <small className="text-muted">{zipcode.state_code}</small>
            </div>
          ) : (
            <span className={column.format === 'number' ? 'font-monospace' : ''}>
              {formatValue((zipcodeWithRank as any)[column.key], column.format)}
            </span>
          )}
        </td>
      ))}
    </tr>
  );
});

TableRow.displayName = 'TableRow';

// Advanced filter component
const AdvancedFilters: React.FC<{
  availableStates: string[];
  availableCities: string[];
  selectedStates: string[];
  selectedCities: string[];
  onStateFilterChange: (states: string[]) => void;
  onCityFilterChange: (cities: string[]) => void;
}> = ({ 
  availableStates, 
  availableCities, 
  selectedStates, 
  selectedCities,
  onStateFilterChange,
  onCityFilterChange 
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  
  const handleStateToggle = (stateCode: string) => {
    const newSelection = selectedStates.includes(stateCode)
      ? selectedStates.filter(s => s !== stateCode)
      : [...selectedStates, stateCode];
    onStateFilterChange(newSelection);
  };

  const hasActiveFilters = selectedStates.length > 0 || selectedCities.length > 0;

  return (
    <div className="dropdown">
      <button
        className={`btn btn-outline-secondary btn-sm dropdown-toggle ${hasActiveFilters ? 'border-primary text-primary' : ''}`}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <i className="fas fa-filter me-1" aria-hidden="true"></i>
        Advanced Filters
        {hasActiveFilters && (
          <span className="badge bg-primary ms-1">
            {selectedStates.length + selectedCities.length}
          </span>
        )}
      </button>
      
      {isOpen && (
        <div className="dropdown-menu show p-3" style={{ minWidth: '300px' }}>
          <h6 className="mb-2">Filter by State</h6>
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => onStateFilterChange(availableStates)}
            >
              All States
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onStateFilterChange([])}
            >
              Clear States
            </button>
          </div>
          
          <div className="border rounded p-2 mb-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
            <div className="row">
              {availableStates.map(stateCode => (
                <div key={stateCode} className="col-6 col-sm-4">
                  <div className="form-check form-check-sm">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`state-${stateCode}`}
                      checked={selectedStates.includes(stateCode)}
                      onChange={() => handleStateToggle(stateCode)}
                    />
                    <label className="form-check-label small" htmlFor={`state-${stateCode}`}>
                      {stateCode}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Zipcode search component with validation
const ZipcodeSearch: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder = "Search zipcodes, cities, states..." }) => {
  const [inputValue, setInputValue] = React.useState(value);
  const [isValid, setIsValid] = React.useState(true);
  
  // Debounce the search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      onChange(inputValue);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [inputValue, onChange]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    
    // Validate if it looks like a zipcode
    if (newValue.length >= 5 && /^\d/.test(newValue)) {
      setIsValid(isValidZipcode(newValue));
    } else {
      setIsValid(true); // Valid for general search
    }
  };

  return (
    <div className="input-group">
      <span className="input-group-text">
        <i className="fas fa-search" aria-hidden="true"></i>
      </span>
      <input
        type="text"
        className={`form-control ${!isValid ? 'is-invalid' : ''}`}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
      />
      {!isValid && (
        <div className="invalid-feedback">
          Please enter a valid 5-digit zipcode (e.g., 12345 or 12345-6789)
        </div>
      )}
    </div>
  );
};

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems: number;
}> = ({ currentPage, totalPages, onPageChange, pageSize, totalItems }) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  if (totalPages <= 1) return null;

  return (
    <div className="d-flex justify-content-between align-items-center">
      <div className="text-muted small">
        Showing {startItem}-{endItem} of {totalItems} zipcodes
      </div>
      <nav aria-label="Table pagination">
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
          </li>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            );
          })}
          
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button
              className="page-link"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

/**
 * ZipcodePerformanceTable Component
 * 
 * A comprehensive data table for displaying zipcode-level performance metrics
 * with sorting, filtering, pagination, and interactive features.
 * 
 * Features:
 * - Sortable columns with visual indicators
 * - Advanced search with zipcode validation
 * - State and city filtering with multi-select
 * - Zipcode format validation and warnings
 * - Pagination with customizable page size
 * - Click handlers for drill-down functionality
 * - Loading states with skeleton rows
 * - Responsive design with horizontal scrolling
 * - Geographic coordinates display
 * - Revenue and ROI metrics
 * 
 * Usage:
 * ```tsx
 * <ZipcodePerformanceTable
 *   data={zipcodesData}
 *   onSort={(column, direction) => handleSort(column, direction)}
 *   onFilter={(filters) => handleFilter(filters)}
 *   onRowClick={(zipcode) => drillDownToZipcode(zipcode)}
 *   pageSize={25}
 * />
 * ```
 */
const ZipcodePerformanceTable: React.FC<ZipcodePerformanceTableProps> = ({
  data,
  isLoading = false,
  onSort,
  onFilter,
  onRowClick,
  pageSize = 25,
  className = ""
}) => {
  // Local state for table functionality
  const [sortConfig, setSortConfig] = React.useState<TableSortConfig | null>({
    column: 'user_count',
    direction: 'desc'
  });
  const [filters, setFilters] = React.useState<TableFilterConfig>({
    searchTerm: '',
    minUserCount: undefined,
    states: []
  });
  const [currentPage, setCurrentPage] = React.useState(1);

  // Get available states and cities for filtering
  const availableStates = React.useMemo(() => {
    const states = new Set(data.map(zipcode => zipcode.state_code));
    return Array.from(states).sort();
  }, [data]);

  const availableCities = React.useMemo(() => {
    const cities = new Set(data.map(zipcode => zipcode.city_name));
    return Array.from(cities).sort();
  }, [data]);

  // Process data with sorting and filtering
  const processedData = React.useMemo(() => {
    let result = filterData(data, filters);
    if (sortConfig) {
      result = sortData(result, sortConfig);
    }
    return result;
  }, [data, filters, sortConfig]);

  // Pagination calculations
  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, currentPage, pageSize]);

  // Data quality metrics
  const dataQuality = React.useMemo(() => {
    const totalZipcodes = data.length;
    const validZipcodes = data.filter(z => isValidZipcode(z.zipcode)).length;
    const invalidZipcodes = totalZipcodes - validZipcodes;
    
    return {
      total: totalZipcodes,
      valid: validZipcodes,
      invalid: invalidZipcodes,
      validPercentage: totalZipcodes > 0 ? (validZipcodes / totalZipcodes) * 100 : 0
    };
  }, [data]);

  // Handle sorting
  const handleSort = React.useCallback((column: string, direction: 'asc' | 'desc') => {
    const newSortConfig = { column, direction };
    setSortConfig(newSortConfig);
    setCurrentPage(1);
    
    if (onSort) {
      onSort(column, direction);
    }
  }, [onSort]);

  // Handle search
  const handleSearchChange = React.useCallback((searchTerm: string) => {
    const newFilters = { ...filters, searchTerm };
    setFilters(newFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Handle min user count filter
  const handleMinUserCountChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const minUserCount = value ? parseInt(value) : undefined;
    const newFilters = { ...filters, minUserCount };
    setFilters(newFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Handle state filter
  const handleStateFilterChange = React.useCallback((states: string[]) => {
    const newFilters = { ...filters, states };
    setFilters(newFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(newFilters);
    }
  }, [filters, onFilter]);

  // Reset filters
  const handleResetFilters = React.useCallback(() => {
    const resetFilters = { searchTerm: '', minUserCount: undefined, states: [] };
    setFilters(resetFilters);
    setCurrentPage(1);
    
    if (onFilter) {
      onFilter(resetFilters);
    }
  }, [onFilter]);

  // Loading skeleton
  const LoadingSkeleton = () => (
    <tbody>
      {Array.from({ length: pageSize }, (_, index) => (
        <tr key={index}>
          {COLUMNS.map(column => (
            <td key={column.key}>
              <div className="placeholder-glow">
                <div className="placeholder col-8" style={{ height: '1rem' }}></div>
              </div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className={`zipcode-performance-table ${className}`.trim()}>
      {/* Data Quality Alert */}
      {dataQuality.invalid > 0 && (
        <div className="alert alert-warning mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
            <div>
              <strong>Data Quality Notice:</strong> {dataQuality.invalid} of {dataQuality.total} zipcodes have invalid formats.
              <small className="ms-2">
                ({dataQuality.validPercentage.toFixed(1)}% valid)
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-4">
          <ZipcodeSearch
            value={filters.searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        <div className="col-md-2">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-users" aria-hidden="true"></i>
            </span>
            <input
              type="number"
              className="form-control"
              placeholder="Min users"
              value={filters.minUserCount || ''}
              onChange={handleMinUserCountChange}
              min="0"
            />
          </div>
        </div>
        <div className="col-md-3">
          <AdvancedFilters
            availableStates={availableStates}
            availableCities={availableCities}
            selectedStates={filters.states || []}
            selectedCities={[]} // Not implemented for cities yet
            onStateFilterChange={handleStateFilterChange}
            onCityFilterChange={() => {}} // Not implemented for cities yet
          />
        </div>
        <div className="col-md-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleResetFilters}
              disabled={!filters.searchTerm && !filters.minUserCount && !filters.states?.length}
            >
              <i className="fas fa-times me-1" aria-hidden="true"></i>
              Clear
            </button>
            <div className="text-muted small align-self-center">
              {processedData.length} zipcodes
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover table-striped">
          <thead className="table-dark">
            <tr>
              {COLUMNS.map(column => (
                <TableHeader
                  key={column.key}
                  column={column}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          
          {isLoading ? (
            <LoadingSkeleton />
          ) : processedData.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">
                  <i className="fas fa-search fa-2x mb-2" aria-hidden="true"></i>
                  <p className="mb-0">
                    {data.length === 0 
                      ? "No zipcode data available" 
                      : "No zipcodes match your search criteria"
                    }
                  </p>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {paginatedData.map((zipcode, index) => (
                <TableRow
                  key={`${zipcode.zipcode}-${zipcode.state_code}`}
                  zipcode={zipcode}
                  columns={COLUMNS}
                  onClick={onRowClick}
                  index={(currentPage - 1) * pageSize + index}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && processedData.length > 0 && (
        <div className="mt-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            totalItems={processedData.length}
          />
        </div>
      )}

      {/* Custom CSS for hover effects */}
      <style>{`
        .cursor-pointer {
          cursor: pointer !important;
        }
        
        .table-row-hover:hover {
          background-color: rgba(13, 110, 253, 0.1) !important;
        }
        
        .user-select-none {
          user-select: none;
        }
        
        th.cursor-pointer:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        .dropdown-menu.show {
          display: block;
          position: absolute;
          z-index: 1000;
        }
      `}</style>
    </div>
  );
};

ZipcodePerformanceTable.displayName = 'ZipcodePerformanceTable';

export default ZipcodePerformanceTable;